import { Box, Button, Chip, Divider, IconButton, Stack, Typography } from "@mui/material";
import { lazy, Suspense, useEffect } from "react";
import {
  ArrowForwardRounded,
  AutoAwesomeOutlined,
  EmailOutlined,
  Facebook,
  HandshakeOutlined,
  Instagram,
  Inventory2Outlined,
  LaunchRounded,
  LocationOnOutlined,
  PhoneOutlined,
  ShoppingBagOutlined,
  StarRounded,
  VerifiedOutlined,
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import SignUpPage from "./SignUp";
import Contact from "./dashboardPages/Contact";
import { useAuth } from "../context/AuthContext";
import { withRusticMediaVersion } from "../utils/mediaUrl";

const AluminumBearHero = lazy(() => import("../components/AluminumBearHero"));

const productImages = [
  "https://api.rusticworldllc.com/media/rustic/product_1678_270_1781240079257.webp_2983dee9bd0a.webp",
  "https://api.rusticworldllc.com/media/rustic/product_1672_263_1781237702030.webp_4c0889a9d424.webp",
  "https://api.rusticworldllc.com/media/rustic/product_1037_759_1741661299009.webp_32eb82c4100e.webp",
  "/get-started-1-back.webp",
  "/get-started-2-back.webp",
  "/get-started-3-back.webp",
].map(withRusticMediaVersion);

const googleMapsUrl =
  "https://www.google.com/maps/place/Rustic+World/@32.5457199,-95.8511881,17z/data=!4m8!3m7!1s0x86490cfb001a858d:0xba1cb2e35da7b78d!8m2!3d32.5457154!4d-95.8486132!9m1!1b1!16s%2Fg%2F11g8v3bw84?entry=ttu&g_ep=EgoyMDI2MDcyOS4wIKXMDSoASAFQAw%3D%3D";

const SectionShell = ({ id, children, dark = false, sx = {} }) => (
  <Box
    id={id}
    sx={{
      scrollMarginTop: 96,
      position: "relative",
      px: { xs: 2.25, sm: 4, md: 7 },
      py: { xs: 9, md: 14 },
      bgcolor: dark ? "#14231a" : "transparent",
      color: dark ? "white" : "text.primary",
      ...sx,
    }}
  >
    <Box sx={{ maxWidth: 1480, mx: "auto", position: "relative", zIndex: 1 }}>{children}</Box>
  </Box>
);

const ProofTile = ({ icon, title, copy }) => (
  <Box
    className="rw-reveal"
    sx={{
      minHeight: 260,
      borderRadius: { xs: 4, md: 5 },
      p: { xs: 3, md: 4 },
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      overflow: "hidden",
      bgcolor: "rgba(255,253,248,.9)",
      border: "1px solid rgba(36,74,56,.12)",
      boxShadow: "0 28px 90px rgba(24,36,29,.11)",
      backdropFilter: "blur(14px)",
    }}
  >
    <Box sx={{ color: "primary.main", "& svg": { fontSize: 36 } }}>{icon}</Box>
    <Box>
      <Typography variant="h4" sx={{ fontSize: { xs: 26, md: 31 }, mb: 1 }}>
        {title}
      </Typography>
      <Typography sx={{ color: "text.secondary", lineHeight: 1.75 }}>{copy}</Typography>
    </Box>
  </Box>
);

const StoryCard = ({ image, title, copy, index }) => (
  <Box
    className="rw-reveal"
    sx={{
      position: "relative",
      minHeight: { xs: 420, md: 520 },
      borderRadius: { xs: 4, md: 5 },
      overflow: "hidden",
      color: "white",
      bgcolor: "#14231a",
      boxShadow: "0 30px 95px rgba(24,36,29,.2)",
      "&:hover .rw-story-img": { transform: "scale(1.055)" },
    }}
  >
    <Box
      className="rw-story-img"
      sx={{
        position: "absolute",
        inset: 0,
        backgroundImage: `url(${image})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        transition: "transform 900ms cubic-bezier(.18,.9,.2,1)",
      }}
    />
    <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, rgba(9,22,15,.94), rgba(9,22,15,.05) 70%)" }} />
    <Box sx={{ position: "relative", minHeight: "inherit", p: { xs: 3, md: 4 }, display: "flex", flexDirection: "column", justifyContent: "space-between" }}>
      <Typography sx={{ fontFamily: '"DM Serif Display", serif', color: "#e4d4bf", fontSize: 32 }}>{String(index).padStart(2, "0")}</Typography>
      <Box>
        <Typography variant="h4" sx={{ mb: 1.25, fontSize: { xs: 28, md: 34 } }}>
          {title}
        </Typography>
        <Typography sx={{ color: "rgba(255,255,255,.72)", lineHeight: 1.75 }}>{copy}</Typography>
      </Box>
    </Box>
  </Box>
);

const Home = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, token } = useAuth();
  const isLogged = Boolean(token && user);
  const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    requestAnimationFrame(() => document.getElementById(id)?.scrollIntoView({ block: "start" }));
  }, [location.hash]);

  return (
    <Box
      component="main"
      id="main-content"
      sx={{
        overflow: "hidden",
        background:
          "radial-gradient(circle at 8% 16%, rgba(184,103,62,.15), transparent 28%), linear-gradient(135deg, #fffaf2 0%, #f1eadf 46%, #e2eadf 100%)",
      }}
    >
      <Box
        id="hero-section"
        sx={{
          minHeight: { xs: 820, md: 820 },
          pt: { xs: 13, md: 16 },
          px: { xs: 2.25, sm: 4, md: 7 },
          pb: { xs: 7, md: 8 },
          position: "relative",
          color: "white",
          display: "grid",
          alignItems: "center",
          background:
            "linear-gradient(116deg, rgba(7,24,16,.99) 0%, rgba(13,64,39,.98) 55%, rgba(217,202,181,.18) 55.2%, rgba(255,250,242,.94) 78%, rgba(255,253,248,.98) 100%)",
        }}
      >
        <Box className="rw-grain" />
        <Box sx={{ maxWidth: 1480, mx: "auto", width: "100%", display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(0, .98fr) minmax(0, 1.02fr)" }, gap: { xs: 7, lg: 8 }, alignItems: "center", position: "relative", zIndex: 1 }}>
          <Box
            className="rw-reveal"
            sx={{
              maxWidth: { xs: "100%", lg: 760 },
              p: { xs: 0, md: 0.5 },
              textShadow: "0 2px 24px rgba(0,0,0,.28)",
            }}
          >
            <Chip
              icon={<AutoAwesomeOutlined />}
              label="Rustic World · Metal, wood, pottery & unusual finds"
              sx={{
                color: "#123522",
                bgcolor: "rgba(255,253,248,.86)",
                border: "1px solid rgba(255,255,255,.4)",
                boxShadow: "0 20px 50px rgba(0,0,0,.12)",
                mb: 3,
                "& .MuiChip-icon": { color: "#b8673e" },
              }}
            />
            <Typography
              component="h1"
              variant="h1"
              sx={{
                maxWidth: 720,
                fontSize: { xs: "3.35rem", sm: "4.55rem", md: "clamp(4.7rem, 5.55vw, 6rem)" },
                lineHeight: 0.94,
                letterSpacing: "-.04em",
                color: "white",
                textWrap: "balance",
                overflowWrap: "break-word",
              }}
            >
              Find the piece everyone asks about.
            </Typography>
            <Typography sx={{ mt: 3, maxWidth: 620, color: "rgba(255,255,255,.9)", fontSize: { xs: 18, md: 21 }, lineHeight: 1.62 }}>
              Rustic World brings together the hard-to-find stuff: vintage-style signs, metal art, warm wood accents, pottery, seasonal pieces and gifts with real personality.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1.35} sx={{ mt: 4, alignItems: { xs: "stretch", sm: "center" } }}>
              <Button
                size="large"
                variant="contained"
                color="secondary"
                endIcon={<ArrowForwardRounded />}
                onClick={() => navigate(isLogged ? "/dashboard" : "/login")}
                sx={{ borderRadius: 999, px: 3.4, py: 1.35, boxShadow: "0 22px 60px rgba(184,103,62,.32)" }}
              >
                {isLogged ? "Shop the finds" : "Sign in to shop"}
              </Button>
              <Button size="large" variant="outlined" onClick={() => scrollTo("signup-section")} sx={{ color: "white", borderColor: "rgba(255,255,255,.36)", borderRadius: 999, px: 3.2, py: 1.25 }}>
                Create an account
              </Button>
            </Stack>
          </Box>

          <Suspense fallback={<Box className="rw-bear-stage rw-bear-stage-placeholder" aria-label="Loading interactive aluminum bear" />}>
            <AluminumBearHero />
          </Suspense>
        </Box>
      </Box>

      <Box className="rw-marquee" aria-hidden="true">
        <Box className="rw-marquee-track">
          {[0, 1, 2, 3].map((item) => (
            <Box component="span" key={item}>
              Vintage-style signs · Metal art · Wood accents · Pottery · Gifts · Seasonal pieces · Canton, Texas ·
            </Box>
          ))}
        </Box>
      </Box>

      <SectionShell id="get-started-section" sx={{ pt: { xs: 8, md: 12 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: ".86fr 1.14fr" }, gap: { xs: 4, md: 7 }, alignItems: "end", mb: { xs: 5, md: 7 } }}>
          <Typography variant="h2" sx={{ fontSize: { xs: "3.25rem", md: "5.6rem" }, lineHeight: .93, letterSpacing: "-.055em", maxWidth: 760 }}>
            Make the room feel found, not furnished.
          </Typography>
          <Typography sx={{ color: "text.secondary", fontSize: { xs: 17, md: 20 }, lineHeight: 1.75, maxWidth: 680 }}>
            From statement wall pieces to small gifts, Rustic World is for people who want texture, color and a little surprise in the things they bring home.
          </Typography>
        </Box>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
          <ProofTile icon={<Inventory2Outlined />} title="Different materials, one place" copy="Metal, wood, pottery, signs, seasonal décor and giftable pieces live together without feeling random." />
          <ProofTile icon={<ShoppingBagOutlined />} title="See it before you ask" copy="Photos, variants and quantities make it easier to choose the right piece without guessing." />
          <ProofTile icon={<HandshakeOutlined />} title="Local shop energy" copy="A Texas storefront feel online: warm, direct and built around pieces people actually talk about." />
        </Box>
      </SectionShell>

      <SectionShell id="about-section" dark sx={{ py: 0, px: 0, overflow: "hidden" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, minHeight: { lg: 820 } }}>
          <Box sx={{ p: { xs: 3, sm: 5, md: 8 }, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <Typography variant="h2" sx={{ fontSize: { xs: "3.5rem", md: "6.2rem" }, lineHeight: .9, letterSpacing: "-.06em", maxWidth: 760 }}>
              Unusual pieces. Easy decisions.
            </Typography>
            <Typography sx={{ mt: 3, color: "rgba(255,255,255,.72)", fontSize: { xs: 17, md: 20 }, lineHeight: 1.8, maxWidth: 680 }}>
              The best pieces do not need a long explanation. They need clear photos, simple choices and a place where customers can come back when they are ready to buy.
            </Typography>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2} sx={{ mt: 5 }}>
              {["Metal", "Wood", "Pottery", "Seasonal"].map((item) => (
                <Chip key={item} label={item} sx={{ color: "white", border: "1px solid rgba(255,255,255,.22)", bgcolor: "rgba(255,255,255,.08)", py: 2.4, fontWeight: 800 }} />
              ))}
            </Stack>
          </Box>
          <Box sx={{ minHeight: { xs: 560, lg: "auto" }, position: "relative", overflow: "hidden" }}>
            <Box sx={{ position: "absolute", inset: 0, backgroundImage: 'url("/About.webp")', backgroundSize: "cover", backgroundPosition: "center", filter: "saturate(.92) contrast(1.08)" }} />
            <Box sx={{ position: "absolute", inset: 0, background: "linear-gradient(0deg, rgba(9,22,15,.62), transparent 55%)" }} />
            <Box className="rw-about-badge">
              <StarRounded />
              <Typography fontWeight={900}>Found in Canton, Texas</Typography>
            </Box>
          </Box>
        </Box>
      </SectionShell>

      <SectionShell id="collection-preview" sx={{ pb: { xs: 8, md: 12 } }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(3, 1fr)" }, gap: 2.5 }}>
          {[
            ["Start with a statement", "One bold sign, flower, pot or wood accent can change the whole corner.", productImages[3]],
            ["Bring the season in", "Holiday, porch and gift pieces that make the house feel alive.", productImages[4]],
            ["Come back for the next find", "Save what catches your eye and build your order when the piece feels right.", productImages[5]],
          ].map(([title, copy, image], index) => (
            <StoryCard key={title} title={title} copy={copy} image={image} index={index + 1} />
          ))}
        </Box>
      </SectionShell>

      <SectionShell sx={{ py: { xs: 8, md: 11 }, background: "linear-gradient(90deg, rgba(20,35,26,.06), rgba(184,103,62,.09))" }}>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: ".9fr 1.1fr" }, gap: 4, alignItems: "center" }}>
          <Typography variant="h2" sx={{ fontSize: { xs: "3rem", md: "5rem" }, lineHeight: .95, letterSpacing: "-.055em" }}>
            Loved locally. Easy to find.
          </Typography>
          <Box sx={{ display: "grid", gap: 2 }}>
            {[
              ["4.8", "Google rating", "See what local visitors say before you visit or order."],
              ["27", "Google reviews", "Real public feedback helps new customers buy with more confidence."],
              ["Canton, TX", "Visit Rustic World", "Find the shop on Veterans Memorial Pkwy and get directions in one tap."],
            ].map(([stat, label, copy]) => (
              <Box key={label} sx={{ p: { xs: 2.5, md: 3 }, borderRadius: 4, bgcolor: "rgba(255,253,248,.94)", border: "1px solid rgba(36,74,56,.16)", boxShadow: "0 24px 70px rgba(24,36,29,.08)" }}>
                <Typography sx={{ fontSize: { xs: 36, md: 48 }, fontWeight: 900, color: "#1b5d3d", lineHeight: 1 }}>{stat}</Typography>
                <Typography sx={{ mt: 0.7, fontSize: { xs: 18, md: 21 }, fontWeight: 900, lineHeight: 1.25 }}>{label}</Typography>
                <Typography sx={{ mt: 0.7, color: "text.secondary", fontWeight: 700, lineHeight: 1.55 }}>{copy}</Typography>
              </Box>
            ))}
            <Button
              href={googleMapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              variant="contained"
              color="primary"
              endIcon={<LaunchRounded />}
              sx={{ justifySelf: { xs: "stretch", sm: "start" }, borderRadius: 999, px: 3.2, py: 1.25 }}
            >
              Read Google reviews
            </Button>
          </Box>
        </Box>
      </SectionShell>

      {!isLogged && (
        <>
          <SectionShell id="signup-section" sx={{ background: "linear-gradient(145deg, #f7f3ec, #ebe1d3)" }}>
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: ".78fr 1.22fr" }, gap: { xs: 5, md: 8 }, alignItems: "center" }}>
              <Box className="rw-reveal">
                <Typography variant="h2" sx={{ fontSize: { xs: "3.1rem", md: "5.2rem" }, lineHeight: .95, letterSpacing: "-.055em" }}>
                  Start with an account made for the way you buy.
                </Typography>
                <Typography sx={{ mt: 2.5, color: "text.secondary", lineHeight: 1.8, fontSize: 18 }}>
                  Create a customer profile, then explore pricing, variants and orders from a cleaner storefront.
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "center" }}>
                <SignUpPage />
              </Box>
            </Box>
          </SectionShell>

        </>
      )}

      <SectionShell id="contact-section" sx={{ background: "#efe6da", py: { xs: 8, md: 11 } }}>
        <Contact />
      </SectionShell>

      <Box component="footer" sx={{ bgcolor: "#0b1710", color: "white", px: { xs: 2.5, md: 7 }, pt: 8, pb: 4 }}>
        <Box sx={{ maxWidth: 1480, mx: "auto" }}>
          <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "1.25fr .7fr .9fr" }, gap: 5 }}>
            <Box>
              <Box component="img" src="/Logo-Rustic.svg" alt="Rustic World" sx={{ height: 62, filter: "brightness(0) invert(1)", mb: 2 }} />
              <Typography sx={{ color: "rgba(255,255,255,.68)", maxWidth: 500, lineHeight: 1.8 }}>
                Metal art, wood accents, pottery, seasonal pieces and giftable finds for homes with character.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 3 }}>
                <IconButton aria-label="Facebook" sx={{ color: "white", border: "1px solid rgba(255,255,255,.18)" }}><Facebook /></IconButton>
                <IconButton aria-label="Instagram" sx={{ color: "white", border: "1px solid rgba(255,255,255,.18)" }}><Instagram /></IconButton>
              </Stack>
            </Box>
            <Box>
              <Typography fontWeight={900} sx={{ mb: 2 }}>Explore</Typography>
              <Stack spacing={1.5}>
                {[["How it works", "get-started-section"], ["Our story", "about-section"], ["Contact", "contact-section"]].map(([label, id]) => (
                  <Typography key={id} onClick={() => scrollTo(id)} sx={{ color: "rgba(255,255,255,.68)", cursor: "pointer", "&:hover": { color: "white" } }}>{label}</Typography>
                ))}
              </Stack>
            </Box>
            <Box>
              <Typography fontWeight={900} sx={{ mb: 2 }}>Visit & connect</Typography>
              <Stack spacing={1.8}>
                {[[<LocationOnOutlined />, "1111 TX-243, Canton, TX"], [<PhoneOutlined />, "+1 (520) 987-1861"], [<EmailOutlined />, "support@rusticworldllc.com"]].map(([icon, text]) => (
                  <Stack key={text} direction="row" spacing={1.5} alignItems="flex-start" sx={{ color: "rgba(255,255,255,.68)" }}>
                    {icon}
                    <Typography variant="body2">{text}</Typography>
                  </Stack>
                ))}
                <Button
                  href={googleMapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  variant="outlined"
                  endIcon={<LaunchRounded />}
                  sx={{ mt: 0.5, color: "white", borderColor: "rgba(255,255,255,.24)", alignSelf: "flex-start", borderRadius: 999 }}
                >
                  Open in Google Maps
                </Button>
              </Stack>
            </Box>
          </Box>
          <Divider sx={{ my: 5, borderColor: "rgba(255,255,255,.12)" }} />
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
            <Typography variant="body2" sx={{ color: "rgba(255,255,255,.52)" }}>© {new Date().getFullYear()} Rustic World. All rights reserved.</Typography>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: "rgba(255,255,255,.52)" }}>
              <VerifiedOutlined fontSize="small" />
              <Typography variant="body2">Secure ordering · local finds · Google reviews</Typography>
            </Stack>
          </Stack>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
